<template>
  <panel-layout
    id="reservationCustomer-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="reservationCustomer && reservation && service" class="flex justify-between items-center">
        <div>
          <h3 class="text-xl tracking-tight text-gray-800 dark:text-gray-100 font-bold leading-none mr-3">
            {{ $t('general.colonSeparator', { label1: service.service.title, label2: reservationCustomer.customer.displayName }) }}
          </h3>

          <p class="text-gray-600 dark:text-gray-300 mt-1 mb-1">
            {{ reservation.dateLabel }}
          </p>
        </div>
      </div>
    </template>

    <template #actions-left>
      <div v-if="isDeleted">
        <o-button
          size="sm"
          variant="warning"
          class="mb-1 mr-1"
          @click="handleRestore"
        >
          {{ $t('reservation.restore') }}
        </o-button>
      </div>

      <div v-if="reservationCustomer && reservation && !isDeleted" class="flex items-center space-x-1">
        <!-- Confirm -->
        <!--If the appointment is unconfirmed (was booked online), show the Approve / Cancel buttons-->
        <template v-if="isUnconfirmed && isBeforeStart">
          <o-button
            v-if="reservationCustomer.allow.confirmCustomer"
            size="sm"
            variant="success"
            icon="reservationConfirm"
            @click="handleConfirm"
          >
            {{ $t('general.confirm') }}
          </o-button>
        </template>

        <!-- Cancel button moved to dropdown menu -->

        <template v-if="isCancelled">
          <o-button
            v-if="reservationCustomer.allow.cancel"
            size="sm"
            outline
            icon="reservationCancel"
            @click="handleUndoCancel"
          >
            {{ $t('reservation.undoCancel') }}
          </o-button>

          <o-button
            v-if="isCourseRunCustomer && reservationCustomer.makeupDetails?.makeupAvailable && !reservationCustomer.makeupDetails?.makeupScheduled"
            size="sm"
            variant="primary"
            @click="handleBookMakeup"
          >
            {{ $t('pages.customer.course.id.scheduleMakeup') }}
          </o-button>
        </template>

        <!--If the current time is prior to the appointment time but after the reminder notification window (of reminder time is null), show the Check-in button.-->
        <!--If the current time is after to the appointment time and not checked in, show the Check-in button and the No-show button.-->
        <template v-if="((isBeforeStart && !isBeforeReminder) || (!isBeforeStart && !isCheckedIn)) && !isUnconfirmed && !isCancelled && !isNoShow">
          <o-button
            v-if="reservationCustomer.allow.checkin"
            size="sm"
            outline
            icon="reservationCheckIn"
            @click="handleCheckIn"
          >
            {{ $t('general.checkIn') }}
          </o-button>
        </template>

        <template v-if="!isBeforeStart && !isCheckedIn && !isCancelled && !isNoShow">
          <o-button
            v-if="reservationCustomer.allow.noShow"
            size="sm"
            outline
            icon="reservationNoShow"
            @click="handleNoShow"
          >
            {{ $t('reservation.noShow') }}
          </o-button>
        </template>

        <!--If the current time is after the appointment time, show the 'Checkout' button. Also show the Re-book button-->
        <template v-if="!isCourseRunCustomer && !isBeforeStart">
          <o-button
            v-if="!reservationCustomer.customerPass && !hasOrder && $grants.all({ order: ['create'] })"
            size="sm"
            outline
            icon="sale"
            @click="handleCreateOrder"
          >
            {{ $t('reservation.checkoutNewSale') }}
          </o-button>

          <o-panel-link
            v-if="hasOrder && $grants.all({ order: ['view'] })"
            size="sm"
            outline
            icon="sale"
            tag="o-button"
            :path="`/order/${reservationCustomer.order.idOrder}`"
          >
            {{ $t('order.view') }}
          </o-panel-link>
        </template>
      </div>
    </template>

    <template #actions-right>
      <div v-if="reservationCustomer && reservation && !isDeleted" class="flex items-center space-x-1">
        <!-- Order -->
        <o-tooltip v-if="!isCourseRunCustomer && reservationCustomer.order && $grants.all({ order: ['view'] })" :text="$t('order.view')">
          <o-panel-link
            icon="sale"
            flat
            tag="o-button"
            :path="`/order/${reservationCustomer.order.idOrder}`"
          />
        </o-tooltip>

        <o-tooltip v-if="!isCourseRunCustomer && !reservationCustomer.customerPass && !reservationCustomer.order && $grants.all({ order: ['create'] })" :text="$t('reservation.checkout')">
          <o-button
            icon="sale"
            flat
            @click="handleCreateOrder"
          />
        </o-tooltip>

        <o-tooltip v-if="!isCourseRunCustomer && reservationCustomer.allow.move" class="h-8" :text="$t('pages.appointment.attendee.id.move')">
          <o-panel-link
            tag="o-button"
            path="/appointment/create"
            :props="{ presets: { move: reservationCustomer } }"
            flat
            icon="reservationMove"
          />
        </o-tooltip>

        <!-- Rebook -->
        <o-tooltip v-if="!isCourseRunCustomer && !reservationCustomer.customer?.isArchived && reservationCustomer.allow.create" class="h-8" :text="$t('reservation.rebook')">
          <o-dropdown>
            <o-button
              size="slim"
              flat
              icon="reservationRebook"
            />

            <template #content>
              <o-panel-link
                v-for="(date, index) in rebook"
                :key="`rebook-week-${index}`"
                :props="{ presets: { reservation: reservationCustomer.reservation, customer: reservationCustomer.customer, rebookReservationCustomer: reservationCustomer, date } }"
                tag="o-dropdown-item"
                path="/appointment/create"
                flat
                variant="info"
              >
                {{ $t('datetime.increments.weeks', { weeks: index + 1 }, index + 1) }}
              </o-panel-link>
            </template>
          </o-dropdown>
        </o-tooltip>

        <!-- Recurring -->
        <o-tooltip v-if="!isCourseRunCustomer && !reservationCustomer.customer?.isArchived && !reservationCustomer.recurrenceGuid && reservationCustomer.allow.create && reservationCustomer.allow.edit" class="h-8" :text="$t('reservation.makeRecurringReservation')">
          <o-button
            size="slim"
            flat
            icon="reservationRecurrence"
            @click="handleRecurrence"
          />
        </o-tooltip>

        <!-- More -->
        <o-dropdown
          placement="bottom-end"
        >
          <o-button
            size="slim"
            flat
            icon="ellipses"
          />

          <template #content>
            <o-dropdown-item v-if="!isCheckedIn && reservationCustomer.allow.checkin" icon="reservationCheckIn" @click="handleCheckIn">
              {{ $t('reservation.checkIn') }}
            </o-dropdown-item>
            <o-dropdown-item v-if="!isNoShow && reservationCustomer.allow.noShow" icon="reservationNoShow" @click="handleNoShow">
              {{ $t('reservation.noShow') }}
            </o-dropdown-item>

            <o-dropdown-item v-if="isCheckedIn && reservationCustomer.allow.checkin" icon="reservationCheckIn" @click="handleUndoCheckIn">
              {{ $t('reservation.undoCheckIn') }}
            </o-dropdown-item>
            <o-dropdown-item v-if="isNoShow && reservationCustomer.allow.noShow" icon="reservationNoShow" @click="handleUndoNoShow">
              {{ $t('reservation.undoNoShow') }}
            </o-dropdown-item>

            <template v-if="integrationsWithActions && reservationCustomer.allow.edit">
              <o-dropdown-divider />

              <o-dropdown-submenu v-for="integration in integrationsWithActions" :key="integration.integration.guid" :icon="integration.integration.icon || 'integrations'">
                {{ integration.integration.name }}

                <template #content>
                  <o-dropdown-item v-for="action in integration.actions" :key="action.code">
                    <span @click="callIntegrationAction(integration.integration, action)">
                      {{ action.title }}
                    </span>
                  </o-dropdown-item>
                </template>
              </o-dropdown-submenu>

              <o-dropdown-divider />
            </template>

            <o-dropdown-submenu v-if="reservationCustomer.allow.email" icon="email">
              {{ $t('general.sendEmail') }}

              <template #content>
                <o-dropdown-item :description="$t('reservation.appointmentReceived')" @click="handleSendEmail(ReservationEmailType.received)">
                  {{ $t('general.reservation') }}
                </o-dropdown-item>
                <o-dropdown-item :description="$t('reservation.appointmentConfirmation')" @click="handleSendEmail(ReservationEmailType.confirmation)">
                  {{ $t('general.confirmation') }}
                </o-dropdown-item>
                <o-dropdown-item :description="$t('reservation.appointmentReminder')" @click="handleSendEmail(ReservationEmailType.reminderEmail)">
                  {{ $t('reservation.reminderEmail') }}
                </o-dropdown-item>
                <o-dropdown-item :description="$t('reservation.appointmentReminderSMS')" @click="handleSendEmail(ReservationEmailType.reminderSMS)">
                  {{ $t('reservation.reminderSMS') }}
                </o-dropdown-item>
                <o-dropdown-item :disabled="isBeforeStart" :description="$t('reservation.reviewRequestInfo')" @click="handleSendEmail(ReservationEmailType.reviewRequest)">
                  {{ $t('reservation.reviewRequest') }}
                </o-dropdown-item>
              </template>
            </o-dropdown-submenu>

            <o-dropdown-item v-if="reservationCustomer.allow.cancel" icon="reservationCancel" variant="danger" @click="handleCancel">
              {{ $t('general.cancel') }}
            </o-dropdown-item>

            <o-dropdown-submenu v-if="!isCourseRunCustomer && reservationCustomer.allow.create" icon="reservationRebook">
              {{ $t('reservation.rebook') }}

              <template #content>
                <o-panel-link
                  v-for="(date, index) in rebook"
                  :key="`rebook-week-${index}`"
                  :props="{ presets: { reservation: reservationCustomer.reservation, customer: reservationCustomer.customer, rebookReservationCustomer: reservationCustomer, date } }"
                  tag="o-dropdown-item"
                  path="/appointment/create"
                  flat
                  variant="info"
                >
                  {{ $t('datetime.increments.weeks', { weeks: index + 1 }, index + 1) }}
                </o-panel-link>
              </template>
            </o-dropdown-submenu>

            <o-dropdown-item icon="reservationRecurrence" @click="handleRecurrence">
              {{ $t('reservation.recurring') }}
            </o-dropdown-item>


            <o-history-link :item="reservationCustomer" component="ODropdownItem" model="ReservationCustomer" icon="history" />

            <o-dropdown-divider />

            <o-dropdown-item v-if="reservationCustomer.allow.delete" icon="delete" variant="danger" @click="handleDelete">
              {{ $t('general.delete') }}
            </o-dropdown-item>
          </template>
        </o-dropdown>
      </div>
    </template>

    <div v-if="reservationCustomer && reservation" class="h-full">
      <o-scroll class="h-full">
        <div class="h-full flex flex-col">
          <div class="pb-8">
            <info v-model:reservationCustomer="reservationCustomer" v-model:loading="loading" @update="handleUpdate" @recurrence="handleRecurrence" @refresh="handleBookMakeupSuccess" />
          </div>
          <div v-if="$features.get('custom-fields') || $features.get('forms')" class="w-full flex-1 min-h-xs">
            <o-tabs nav-class="px-5" container-class="py-5 bg-gray-50/0 h-full dark:" scroll>
              <o-tab v-if="$features.get('custom-fields') && reservationCustomer.customFields.length" :name="$t('general.customFields')" icon="customFields">
                <fields :reservation="reservationCustomer" />
              </o-tab>
              <o-tab v-if="$features.get('forms') && ((reservationCustomer.forms && reservationCustomer.forms.length) || (reservation.relatedForms && reservationCustomer.relatedForms.length))" :name="$t('general.forms')" icon="forms">
                <forms :reservation="reservationCustomer" />
              </o-tab>
              <o-tab v-if="$features.get('service-automation') && hasAutomations" :name="$t('general.aftercare')" icon="sparkles">
                <aftercare v-model:reservation="reservationCustomer" v-model:loading="loading" @update="handleUpdate" />
              </o-tab>
              <o-tab :name="$t('general.uploads')" icon="upload">
                <uploads :reservation="reservationCustomer" />
              </o-tab>
            </o-tabs>
          </div>
        </div>
      </o-scroll>
    </div>

    <template #footer>
      <div v-if="reservationCustomer && reservation" class="flex items-center justify-end space-x-1">
        <div>
          <o-button variant="primary" icon="save" @click="save()">
            {{ $t('general.save') }}
          </o-button>
          <o-button variant="primary" icon="save" @click="save(true)">
            {{ $t('general.saveAndClose') }}
          </o-button>
        </div>
      </div>
    </template>

    <template v-if="reservationCustomer && reservation">
      <confirm-dialog
        ref="confirmDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        v-model:loading="loading"
      />

      <check-in-dialog
        ref="checkInDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        v-model:loading="loading"
      />

      <undo-check-in-dialog
        ref="undoCheckInDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        v-model:loading="loading"
      />

      <cancel-dialog
        ref="cancelDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        :cancellation-types="reservationCustomer.options.cancellationTypes || []"
        v-model:loading="loading"
      />

      <undo-cancel-dialog
        ref="undoCancelDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        v-model:loading="loading"
      />

      <no-show-dialog
        ref="noShowDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        v-model:loading="loading"
      />

      <undo-no-show-dialog
        ref="undoNoShowDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        v-model:loading="loading"
      />

      <send-email-dialog
        ref="sendEmailDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        v-model:loading="loading"
      />

      <delete-dialog
        ref="deleteDialog"
        :portal="panel.portal"
        v-model:reservation="reservationCustomer"
        v-model:loading="loading"
        @success="cancel(true)"
      />

      <recurrence-modal
        ref="recurrenceModal"
        :portal="panel.portal"
        v-model:loading="loading"
        @success="() => fetchReservationCustomer(componentProps.id)"
      />

      <book-makeup-dialog
        v-if="reservationCustomer.customerCourse"
        ref="bookMakeupDialog"
        :portal="panel.portal"
        v-model:loading="loading"
        @success="handleBookMakeupSuccess"
      />
    </template>

    <o-dialog
      ref="unsavedDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import Info from '@/components/reservationCustomer/edit/Info.vue'

// @ts-ignore
import Fields from '@/components/reservation/edit/Fields.vue'
// @ts-ignore
import Communications from '@/components/reservation/edit/Communications.vue'
// @ts-ignore
import Forms from '@/components/reservation/edit/Forms.vue'
// @ts-ignore
import Aftercare from '@/components/reservation/edit/Aftercare.vue'
// @ts-ignore
import Uploads from '@/components/reservationCustomer/edit/Uploads.vue'

// @ts-ignore
import CheckInDialog from '@/components/reservationCustomer/actions/CheckIn.vue'
// @ts-ignore
import UndoCheckInDialog from '@/components/reservation/actions/UndoCheckIn.vue'
// @ts-ignore
import ConfirmDialog from '@/components/reservationCustomer/actions/Confirm.vue'
// @ts-ignore
import RecurrenceModal from '@/components/reservationCustomer/actions/Recurrence.vue'
// @ts-ignore
import CancelDialog from '@/components/reservation/actions/Cancel.vue'
// @ts-ignore
import DeleteDialog from '@/components/reservation/actions/Delete.vue'
// @ts-ignore
import UndoCancelDialog from '@/components/reservation/actions/UndoCancel.vue'
// @ts-ignore
import NoShowDialog from '@/components/reservation/actions/NoShow.vue'
// @ts-ignore
import UndoNoShowDialog from '@/components/reservation/actions/UndoNoShow.vue'
// @ts-ignore
import SendEmailDialog from '@/components/reservation/actions/SendEmail.vue'
// @ts-ignore
import BookMakeupDialog from '@/components/customerCourse/actions/BookMakeup.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'


// @ts-ignore
import {
  ODropdown,
  ODropdownItem,
  ODropdownDivider,
  ODropdownSubmenu,
  OTabs,
  OTab,
} from '@/components/elements'

import { ReservationEmailType } from '~/_api/models/reservation'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const componentProps = defineProps<{ id?: string; date?: string }>()

const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
}>()

const { $api, $bus, $notification, $auth, $date, $i18n } = useNuxtApp()
const { createOrder } = useOrderCreate()

const uid = Math.random().toString(36).slice(2)

// Template refs
const confirmDialog = ref<any>()
const checkInDialog = ref<any>()
const undoCheckInDialog = ref<any>()
const cancelDialog = ref<any>()
const undoCancelDialog = ref<any>()
const deleteDialog = ref<any>()
const noShowDialog = ref<any>()
const undoNoShowDialog = ref<any>()
const sendEmailDialog = ref<any>()
const recurrenceModal = ref<any>()
const bookMakeupDialog = ref<any>()
const unsavedDialog = ref<any>()

// Data
const reservationCustomer = ref<any>(null)
const errors = ref<any>(null)
const loading = ref(true)
// ReservationEmailType imported directly from ~/_api/models/reservation

const provider = reactive({
  unsavedChanges: false,
  save,
})

provide('provider', provider)

// Computed
const reservation = computed(() => reservationCustomer.value ? reservationCustomer.value.reservation : null)
const service = computed(() => reservationCustomer.value?.reservation?.services?.[0] ?? null)

const rebook = computed(() => {
  const rebookDates: any[] = []
  let date = reservation.value.date.date

  for (let i = 0; i < 12; i++) {
    date = $date.add(date, { weeks: 1 })
    rebookDates.push(date)
  }

  return rebookDates
})

const { unsavedChanges, snapshot } = useUnsavedChanges(reservationCustomer)

const isConfirmed = computed(() => reservationCustomer.value.isStatusConfirmed)
const isCancelled = computed(() => reservationCustomer.value.isStatusCancelled)
const isDeleted = computed(() => reservationCustomer.value?.deleted)
const isNoShow = computed(() => reservationCustomer.value.noShow)
const isUnconfirmed = computed(() => reservationCustomer.value.isStatusUnconfirmed)

const isBeforeStart = computed(() => {
  const today = $date.utc(new Date())
  const start = reservationCustomer.value?.reservation?.startTimeUTC
  return $date.isBefore(today, start)
})

const isBeforeReminder = computed(() => {
  return $date.isBefore(new Date(), reservationCustomer.value.reservation.reminderTime * 1000)
})

const isCheckedIn = computed(() => reservationCustomer.value.checkedIn)
const isCourseRunCustomer = computed(() => !!reservationCustomer.value.customerCourse)
const hasOrder = computed(() => !!reservationCustomer.value.order)
const hasCustomerPass = computed(() => !!reservationCustomer.value.customerPass)
const hasAutomations = computed(() => reservationCustomer.value.automations && reservationCustomer.value.automations.length > 0)

const integrationsWithActions = computed(() => {
  if (reservationCustomer.value.integrations) {
    return reservationCustomer.value.integrations.filter((integration: any) => integration.actions.length > 0)
  }

  return null
})

// Watchers
watch(reservationCustomer, () => {
  setTimeout(() => {
    $bus.emit.updated('ReservationCustomer', reservationCustomer.value)
  }, 500)
})

watch(unsavedChanges, (value) => {
  provider.unsavedChanges = value
})

// Methods
const getLocationTime = () => {
  const offset = $auth.info?.location.timeZoneOffset || 0
  const date = new Date(new Date().toUTCString().slice(0, -4))
  const time = $date.add(date, { seconds: offset })
  const minutes = (time as any).getMinutes()
  const hours = (time as any).getHours()

  return (hours * 60) + minutes
}

const handleCheckIn = () => {
  checkInDialog.value.open()
}

const handleUndoCheckIn = () => {
  undoCheckInDialog.value.open()
}

const handleCancel = () => {
  cancelDialog.value.open()
}

const handleDelete = () => {
  deleteDialog.value.open()
}

const handleConfirm = () => {
  confirmDialog.value.open()
}

const handleUndoCancel = () => {
  undoCancelDialog.value.open()
}

const handleNoShow = () => {
  noShowDialog.value.open()
}

const handleUndoNoShow = () => {
  undoNoShowDialog.value.open()
}

const handleSendEmail = (type: any) => {
  sendEmailDialog.value.open(type)
}

const handleRecurrence = () => {
  recurrenceModal.value.open(reservationCustomer.value)
}

const handleBookMakeup = () => {
  bookMakeupDialog.value.open(
    reservationCustomer.value.customerCourse,
    reservationCustomer.value.idReservationCustomer,
  )
}

const handleBookMakeupSuccess = () => {
  fetchReservationCustomer(componentProps.id)
}

const handleRestore = async () => {
  try {
    loading.value = true

    const response = await $api.reservationCustomer.restoreReservationCustomer(reservationCustomer.value.idReservationCustomer.toString())

    if (response) {
      $notification.toast({
        title: $i18n.t('reservation.restoredSuccess'),
        text: $i18n.t('reservation.restoredSuccessInfo'),
        variant: 'success',
      })

      $bus.emit.updated('ReservationCustomer', reservationCustomer.value)

      fetchReservationCustomer(componentProps.id)
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const handleCreateOrder = async () => {
  emit('update:loading', true)

  const customer = reservationCustomer.value.customer
  const items = [reservationCustomer.value]

  await createOrder({ items, customer })

  emit('update:loading', false)
}

const callIntegrationAction = async (integration: any, action: any) => {
  if (action.isCallback) {
    emit('update:loading', true)

    try {
      const rc = (await $api.reservationCustomer.integrationAction(
        reservationCustomer.value.idReservationCustomer.toString(),
        { guid: integration.guid, actionCode: action.code }
      )).data

      if (rc) {
        emit('update:loading', false)
      }
    } catch (e) {
      console.log(e)
    }
  } else if (action.isInternalUrl) {
    // open internal URL
  } else if (action.isExternalUrl) {
    window.open(action.url)
  }
}

const fetchReservationCustomer = async (id?: string) => {
  loading.value = true

  try {
    if (id) {
      const rc = (await $api.reservationCustomer.get(id)).data
      reservationCustomer.value = rc
      snapshot()
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

async function save (close = false) {
  loading.value = true
  errors.value = false

  try {
    const rc = (await $api.reservationCustomer.save(reservationCustomer.value)).data

    if (rc) {
      reservationCustomer.value = rc
      snapshot()

      $notification.toast({
        title: $i18n.t('reservation.attendeeSavedSuccess'),
        text: $i18n.t('reservation.attendeeSavedSuccessInfo'),
        variant: 'success',
      })

      $bus.emit.updated('ReservationCustomer', rc)
      $bus.emit.updated('Reservation', rc.reservation)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    unsavedDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleUpdate = () => {
  $bus.emit.updated('ReservationCustomer', reservationCustomer.value)
  $bus.emit.updated('Reservation', reservationCustomer.value.reservation)
  snapshot()
}

watch(() => componentProps.id, (id) => fetchReservationCustomer(id), { immediate: true })

onBeforeUnmount(() => {
  $bus.off.all(uid)
})
</script>
